import { DateOnly } from '@if/core/date'
import { t } from '@if/core/i18n'
import { dateOnly, double, InferSubmit, object, ref } from '@if/core/yup'
import { useIfUser } from '@if/mui'
import { EPaymentMethod } from '@src/types/constants'
import { CalendarSimulation } from '@src/types/loan/loan-simulation'
import { useMemo } from 'react'
import { previewedSimulation } from '../LoanCalendarActionPreview/LoanCalendarActionPreview-schema'

/** The simulation is closed over, so the rules read it directly with no validation context. */
export const getChangeRegularPmtSchema = (simulation: CalendarSimulation, canBypassAcpLock: boolean) =>
  object({
    amount: double()
      .required()
      .moreThan(0)
      .test(
        'test-sameAmount',
        () => t('loan.payment.changeRegularPmtDialog.errors.sameAmount'),
        (value, context) => {
          const fromDate = context.resolve<DateOnly | null>(ref('fromDate'))
          if (!fromDate) return true
          return value !== simulation.getRepaymentConfigAtDate(fromDate).amount
        },
      ),
    fromDate: dateOnly()
      .default(null)
      .required()
      .test(
        'test-validMinimumFromDate',
        () => t('loan.payment.changeRegularPmtDialog.errors.invalidFromDate'),
        (value) => {
          if (!value) return true
          return value >= simulation.getNextValidActionDate(EPaymentMethod.PreAuthorizedDebit, canBypassAcpLock)
        },
      )
      .test(
        'test-validPaymentDate',
        () => t('loan.payment.changeRegularPmtDialog.errors.invalidPaymentDate'),
        (value) => {
          if (!value) return true
          const operations = simulation.operationInformations
          if (!operations) return true
          return operations.some(
            (oi) => oi.isCustomerPaymentOperation() && oi.operation.effectiveDate.getTime() === value.getTime(),
          )
        },
      ),
    previewedSimulation: previewedSimulation(),
  })

export type ChangeRegularPmtSchemaType = InferSubmit<ReturnType<typeof getChangeRegularPmtSchema>>

export const useChangeRegularPmtSchema = (simulation: CalendarSimulation) => {
  const user = useIfUser()
  const canBypassAcpLock = user.rights.canEditLockedPpaPayments
  return useMemo(() => getChangeRegularPmtSchema(simulation, canBypassAcpLock), [simulation, canBypassAcpLock])
}
